import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Typography,
  Popconfirm,
  Modal,
  Form,
  Select,
  Input,
  App,
  Result,
} from 'antd';
import {
  DownloadOutlined,
  SendOutlined,
  LinkOutlined,
  ReloadOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../Components/common/PageHeader';
import { LoadingState, ApiErrorState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import cfoService from '../../Services/cfoService';
import {
  InvoiceStatus,
  InvoiceStatusLabels,
  InvoiceStatusColors,
  InvoicePaymentMethodLabels,
  InvoicePaymentMethod,
  labelOf,
} from '../../Constants/enums';
import { money, formatDate, formatDateTime } from '../../Utils/format';
import { downloadBlob, filenameFromDisposition } from '../../Utils/download';
import { toApiError } from '../../api/axiosClient';

// Offline confirmation excludes Stripe (0) - Stripe is confirmed automatically.
const OFFLINE_METHODS = [
  InvoicePaymentMethod.BankTransfer,
  InvoicePaymentMethod.Direct,
  InvoicePaymentMethod.Other,
].map((v) => ({ value: v, label: labelOf(InvoicePaymentMethodLabels, v) }));

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { data, loading, error, reload } = useApiData(() => cfoService.getInvoice(id), [id]);

  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [form] = Form.useForm();

  if (error?.status === 404) {
    return (
      <Result
        status="404"
        title="Invoice not found"
        extra={<Button onClick={() => navigate('/cfo/invoices')}>Back to invoices</Button>}
      />
    );
  }

  const isDraft = data?.status === InvoiceStatus.Draft;
  const isPaid = data?.status === InvoiceStatus.Paid;
  const isVoided = data?.status === InvoiceStatus.Voided;

  const onDownload = async () => {
    setBusy(true);
    try {
      const response = await cfoService.downloadInvoicePdf(id);
      const filename = filenameFromDisposition(
        response.headers?.['content-disposition'],
        `${data.invoiceNumber || 'invoice'}.pdf`
      );
      downloadBlob(response.data, filename);
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const onSend = async () => {
    setBusy(true);
    try {
      const result = await cfoService.sendInvoice(id);
      message.success('Invoice sent. The billing contact received the invoice and checkout link.');
      reload();
      const url = result?.checkoutUrl;
      if (url) {
        Modal.confirm({
          title: 'Invoice sent',
          content: 'A Stripe checkout link was created. Open it now?',
          okText: 'Open payment link',
          cancelText: 'Later',
          onOk: () => window.open(url, '_blank', 'noopener'),
        });
      }
    } catch (err) {
      const { status, message: msg } = toApiError(err);
      message.error(status === 502 ? 'Stripe could not create the checkout session. Please retry.' : msg);
    } finally {
      setBusy(false);
    }
  };

  const onConfirmOffline = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid — field errors render inline
    }
    setConfirming(true);
    try {
      await cfoService.confirmPayment(id, values);
      message.success('Offline payment confirmed.');
      setConfirmOpen(false);
      form.resetFields();
      reload();
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setConfirming(false);
    }
  };

  const lineColumns = [
    { title: 'Description', dataIndex: 'description' },
    { title: 'Qty', dataIndex: 'quantity' },
    { title: 'Unit price', dataIndex: 'unitPrice', render: (v) => money(v, data?.currency) },
    { title: 'Amount', dataIndex: 'amount', render: (v) => money(v, data?.currency) },
  ];

  const headerActions = data ? (
    <Space wrap>
      <Button icon={<ReloadOutlined />} onClick={reload}>Refresh</Button>
      <Button icon={<DownloadOutlined />} onClick={onDownload} loading={busy}>Download PDF</Button>
      {isDraft && (
        <Popconfirm
          title="Send this invoice? This creates the Stripe checkout link and emails the billing contact."
          okText="Send"
          cancelText="Cancel"
          onConfirm={onSend}
          okButtonProps={{ loading: busy }}
        >
          <Button type="primary" icon={<SendOutlined />} loading={busy}>Send Invoice</Button>
        </Popconfirm>
      )}
      {data.checkoutUrl && !isPaid && (
        <Button icon={<LinkOutlined />} onClick={() => window.open(data.checkoutUrl, '_blank', 'noopener')}>
          Open Payment Link
        </Button>
      )}
      {!isPaid && !isVoided && !isDraft && (
        <Button icon={<CheckOutlined />} onClick={() => setConfirmOpen(true)}>
          Confirm Offline Payment
        </Button>
      )}
    </Space>
  ) : null;

  return (
    <div>
      <PageHeader
        title={
          data ? (
            <Space>
              {data.invoiceNumber}
              <StatusTag value={data.status} labels={InvoiceStatusLabels} colors={InvoiceStatusColors} />
            </Space>
          ) : (
            'Invoice'
          )
        }
        subtitle={data ? `${data.clientName} · ${data.accountNumber}` : undefined}
        docTitle={data?.invoiceNumber || 'Invoice'}
        onBack={() => navigate('/cfo/invoices')}
        extra={headerActions}
      />

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={10}>
              <Card title="Invoice" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Total">
                    <strong>{money(data.total, data.currency)}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Currency">{data.currency}</Descriptions.Item>
                  <Descriptions.Item label="Issue date">{formatDate(data.issueDate)}</Descriptions.Item>
                  <Descriptions.Item label="Due date">{formatDate(data.dueDate)}</Descriptions.Item>
                  <Descriptions.Item label="Sent">{formatDateTime(data.sentAtUtc)}</Descriptions.Item>
                  <Descriptions.Item label="Paid">{formatDateTime(data.paidAtUtc)}</Descriptions.Item>
                  <Descriptions.Item label="Payment method">
                    {data.paymentMethod === null || data.paymentMethod === undefined
                      ? '-'
                      : labelOf(InvoicePaymentMethodLabels, data.paymentMethod)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Payment reference">{data.paymentReference || '-'}</Descriptions.Item>
                </Descriptions>
                {data.checkoutUrl && (
                  <Typography.Paragraph copyable={{ text: data.checkoutUrl }} style={{ marginTop: 8 }} ellipsis>
                    {data.checkoutUrl}
                  </Typography.Paragraph>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card title="Line items" size="small">
                <Table
                  rowKey={(r) => r.id ?? r.description}
                  columns={lineColumns}
                  dataSource={data.lines || []}
                  pagination={false}
                  size="small"
                  scroll={{ x: 'max-content' }}
                />
              </Card>
            </Col>
          </Row>

          <Modal
            title="Confirm Offline Payment"
            open={confirmOpen}
            onOk={onConfirmOffline}
            onCancel={() => setConfirmOpen(false)}
            confirmLoading={confirming}
            okText="Confirm payment"
            destroyOnHidden
          >
            <Typography.Paragraph type="secondary">
              Use this only for a verified bank transfer, direct, or other offline payment. Stripe payments confirm automatically.
            </Typography.Paragraph>
            <Form form={form} layout="vertical" requiredMark="optional">
              <Form.Item name="paymentMethod" label="Payment method" rules={[{ required: true }]}>
                <Select options={OFFLINE_METHODS} placeholder="Select method" />
              </Form.Item>
              <Form.Item name="paymentReference" label="Payment reference" rules={[{ required: true }]}>
                <Input placeholder="e.g. BANK-TRX-20481" />
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}
    </div>
  );
};

export default InvoiceDetail;
